import { Button } from 'components';
import * as Styled from './styles';
import { useEffect, useRef, useState } from 'react';
import theme from 'styles/theme';
import { FaSearch } from "react-icons/fa";
import { getFilterValues } from 'services/filterRequests';
import loadingIcon from 'assets/icone/icone_loading.gif';
import { BsInfoLg } from 'react-icons/bs';
import { IRows } from 'contexts/@types/TabelaAtributos';
import { BaseSelect } from 'components/BaseSelect';
import { useTabelaAtributos } from 'hooks/useTabelaAtributos';
import { ILayers } from 'contexts/@types/Layers';
import { TbFilterExclamation } from 'react-icons/tb';
import { clearText } from 'utils/clearText';

interface IAdvancedFilter {
  // tab: ITab;
  rows: IRows;
  onFilter: (query: string, layerUuid?: string, registrar?: boolean) => void;
  appliedFilter: string | null;
  selectedLayerUuid: string;
  onSelectLayer: (value: string) => void;
}

interface ITempFilter {
  valor: string;
  field: string;
}

export function FiltroAvancado({ onFilter, appliedFilter, selectedLayerUuid, onSelectLayer }: IAdvancedFilter) {

  useEffect(() => {
    setCampoSelected(0);
    if (appliedFilter) {
      setFilter(appliedFilter);
      setTempFilter(undefined);
      onFilter(appliedFilter.trimStart(), selectedLayerUuid || undefined);
    } else {
      setFilter('');
      setTempFilter(undefined);
      onFilter('', selectedLayerUuid || undefined);
    }
  }, [appliedFilter, selectedLayerUuid])

  const [campoSelected, setCampoSelected] = useState<number>(0);
  const [filter, setFilter] = useState<string>('');
  const [tempFilter, setTempFilter] = useState<ITempFilter>();

  const [valueFilter, setValueFilter] = useState<string>('');
  const [fieldFilter, setFieldFilter] = useState<string>('');

  const [values, setValues] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [loadingValues, setLoadingValues] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const valueTableRef = useRef<HTMLDivElement>(null);
  const scrollRequestedRef = useRef<boolean>(false);

  const { tabs } = useTabelaAtributos();
  const selectedTabIndex = selectedLayerUuid
    ? tabs.findIndex(tabItem => getLayerUuid(tabItem.layer) === selectedLayerUuid)
    : -1;
  const selectedTab = selectedTabIndex >= 0 ? tabs[selectedTabIndex] : undefined;
  const selectedHeader = selectedTab?.headers?.[campoSelected];

  function getLayerUuid(layer: ILayers) {
    return `${layer.layertitle}${layer.layer_filter_id || ''}`;
  }

  const operadores = [
    { label: '=', value: '=' },
    { label: '<>', value: '<>' },
    { label: '>', value: '>' },
    { label: '>=', value: '>=' },
    { label: '<', value: '<' },
    { label: '<=', value: '<=' },
    { label: 'CONTÉM', value: 'LIKE', tooltip: 'Diferencia letras maiúsculas de minúsculas' },
    { label: 'E', value: 'AND' },
    { label: 'OU', value: 'OR' },
    { label: 'NULL', value: 'IS NULL', reset: true },
    { label: 'NOT NULL', value: 'IS NOT NULL', reset: true },
  ];

  const disabledLayers = tabs
    .filter(tab => tab.layer?.layertype === 'VECTOR')
    .map(tab => getLayerUuid(tab.layer));

  function isNumberField() {
    const type = selectedHeader?.ds_tipo || '';
    return type === 'N\u00famero' || type === 'Numero';
  }

  function getColor(id: number, selected?: number): string {
    if (selected !== undefined && id === selected) return theme.colors.blue100;
    if (id % 2 === 0) return theme.colors.white;
    return theme.colors.gray100;
  }

  function onChangeCampo(valor: string, id: number) {
    if (tempFilter && tempFilter.field !== 'campo') {
      setFilter(old => `${old} ${tempFilter.valor}`);
    }
    setTempFilter({ valor: valor, field: 'campo' })
    setCampoSelected(id);
  }

  function onChangeOperador(op: { label: string; value: string; reset?: boolean }) {
    if (tempFilter && tempFilter.field !== 'operador') {
      setFilter(old => `${old} ${tempFilter.valor}`);
    }
    setTempFilter({ valor: op.value, field: op.reset ? 'valor' : 'operador' });
  }

  function onChangeValue(valor: string) {
    if (campoSelected === undefined) return;

    let treatedValue = valor;

    if (isNumberField()) treatedValue = valor.toString();
    else {
      if (tempFilter?.valor === 'LIKE') treatedValue = `'%${valor}%'`;
      else treatedValue = `'${valor}'`;
    }

    if (tempFilter && tempFilter.field !== 'valor') {
      setFilter(old => `${old} ${tempFilter.valor}`);
    }
    setTempFilter({ valor: treatedValue, field: 'valor' })
  }

  function onChangeFilter(evt: React.ChangeEvent<HTMLTextAreaElement>) {
    setFilter(evt.target.value);
    setTempFilter(undefined);
  }

  function getFilter() {
    if (tempFilter) return `${filter} ${tempFilter.valor}`.trimStart();
    return filter.trimStart();
  }

  function clearFilter() {
    setFilter('');
    onFilter('', selectedLayerUuid || undefined);
    setTempFilter(undefined);
  }

  useEffect(() => {
    const container = valueTableRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (loadingValues) return;
      if (scrollRequestedRef.current) return;

      const nearBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight / 2;

      if (nearBottom && currentPage < totalPages) {
        scrollRequestedRef.current = true;
        getValues(false)
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [values, currentPage]);

  function getValues(reset = false) {
    if (campoSelected === undefined) return;
    if (!selectedTab || !selectedHeader?.nm_coluna) return;
    if (reset) setLoadingValues(true);

    getFilterValues(
      selectedTab.layer.id,
      selectedHeader.nm_coluna,
      selectedTab.layer_query_filter,
      valueFilter,
      isNumberField(),
      reset ? 1 : currentPage + 1,
    ).then((response) => {
      setValues(v => reset ? response.values : [...v, ...response.values])
      setTotalPages(response.total_pages);
      setCurrentPage(reset ? 1 : currentPage + 1)
      scrollRequestedRef.current = false;
    })
    .finally(() => {
      setLoadingValues(false)
    })
  }

  useEffect(() => {
    if (!selectedTab?.headers?.length) return;
    if (!selectedHeader) {
      setCampoSelected(0);
    }
  }, [selectedTab, selectedHeader]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (campoSelected !== undefined) {
        getValues(true)
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [valueFilter])

  useEffect(() => {
    setValueFilter('');
    if (campoSelected !== undefined && selectedHeader?.nm_coluna) {
      getValues(true)
    }
  }, [campoSelected, selectedTab, selectedHeader])

  function clearIncludes(value: string, query: string) {
    return clearText(value).includes(clearText(query));
  }

  function getFilteredValues() {
    return selectedTab!.headers.filter(h => h.nm_alias ? clearIncludes(h.nm_alias, fieldFilter) : clearIncludes(h.nm_coluna, fieldFilter));
  }
  
  return (
    <Styled.Content>
      <BaseSelect
        value={selectedLayerUuid || 'disable'}
        onChange={onSelectLayer}
        placeholder={'Selecione a camada'}
        options={tabs.map(tabItem => ({ label: tabItem.layerTitle, value: getLayerUuid(tabItem.layer) }))}
        disabledLayers={disabledLayers}
        displayIcon={disabledLayers.length == 0 ? false : true}
        tooltipeMessage='Filtro avançado não disponível para camadas externas'
      />
      {!selectedTab &&
        <div className="error-msg">
          <TbFilterExclamation className="watermark" />
          Escolha uma camada para realizar o filtro
        </div>
      }
      {selectedTab &&
        <>
          <div className='values-wrapper'>
            <div className="field-wrapper">
              <div className='title'>Campos</div>
              <div className='field-input'>
                <div className='input-wrapper'>
                  <span className='search-icon'>
                    <FaSearch />
                  </span>
                  <input className='search-input' value={fieldFilter} onChange={e => setFieldFilter(e.target.value)}>
                  </input>
                </div>
                <div className='values'>
                  {!selectedTab || getFilteredValues().length === 0 &&
                    <div className='notFound'>
                      Nenhum resultado encontrado.
                    </div>
                  }
                  {selectedTab! &&
                    getFilteredValues().map((element) => (
                      <div
                        className='value-row'
                        key={element.nm_coluna}
                        style={{
                          backgroundColor: getColor(selectedTab.headers.indexOf(element), campoSelected),
                          cursor: 'pointer',
                        }}
                        onClick={() => onChangeCampo(element.nm_coluna, selectedTab.headers.indexOf(element))}
                      >
                        <div
                          style={{
                            padding: '4px 4px 4px 4px'
                          }}
                        >
                          {element.nm_alias ?? element.nm_coluna}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
            <div className="field-wrapper">
              <div className='title'>Valores</div>
              <div className='field-input'>
                <div className='input-wrapper'>
                  <span className='search-icon'>
                    <FaSearch />
                  </span>
                  <input className='search-input' value={valueFilter} onChange={e => setValueFilter(e.target.value)}>
                  </input>
                </div>
                <div className='values' ref={valueTableRef}>
                  {values.length === 0 &&
                    <div className='notFound'>
                      Nenhum resultado encontrado.
                    </div>
                  }
                  {loadingValues ?
                    <Styled.Loading>
                      <img src={loadingIcon} alt="Loading" />
                    </Styled.Loading> :
                    campoSelected !== undefined &&
                    values.map((element, idx) => (
                      <div
                        className='value-row'
                        key={idx}
                        style={{
                          backgroundColor: getColor(idx),
                          cursor: 'pointer',
                        }}
                        onClick={() => onChangeValue(element)}
                      >
                        <div
                          style={{
                            padding: '4px 4px 4px 4px'
                          }}
                        >
                          {element}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          </div>

          <div className="operator-wrapper">
            <div className="operator-field">
              <div className='title'>Operadores</div>
              <div className='operator-input operators'>
                {
                  operadores.map((op, id) => (
                    <Styled.OpButton
                      key={`botao_operador_${id}`}
                      onClick={() => onChangeOperador(op)}
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                      label={op.tooltip}
                    >
                      {op.label}
                      {op.tooltip &&
                        <BsInfoLg className='operator-info' />
                      }
                    </Styled.OpButton>
                  ))
                }
              </div>
            </div>
            <div className="operator-field">
              <div className='title'>Expressão do filtro</div>
              <div className='operator-input'>
                <textarea
                  className='text-area'
                  value={getFilter()}
                  onChange={onChangeFilter}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const finalFilter = getFilter();
                      setFilter(finalFilter);
                      setTempFilter(undefined);
                      onFilter(finalFilter, selectedLayerUuid || undefined, true);
                    };
                  }}
                />
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <div className='button-wrapper'>
              <Button
                className="button clear"
                onClick={clearFilter}
              >
                Limpar Filtro
              </Button>
              <Button
                className="button"
                onClick={() => {
                  const finalFilter = getFilter();
                  setFilter(finalFilter);
                  setTempFilter(undefined);
                  onFilter(finalFilter, selectedLayerUuid || undefined, true);
                }}
              >
                Executar
              </Button>
            </div>
          </div>
        </>
      }
    </Styled.Content>
  );
}
